import React, { useState, useEffect, useCallback } from 'react';
import emailOutreachApi from '../api/emailOutreachApi';

function DeliverabilityTab() {
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await emailOutreachApi.getDeliverabilityStats({ days });
      if (res.success && res.data) {
        setStats(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load deliverability metrics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const rates = stats?.rates || {};
  const bounceRate = rates.bounceRate ?? 0;
  const complaintRate = rates.complaintRate ?? 0;
  const replyRate = rates.replyRate ?? 0;
  const unsubscribeRate = rates.unsubscribeRate ?? 0;

  // Threshold alerts: bounce rate > 3% or complaint rate > 0.1%
  const hasBounceWarning = bounceRate > 3.0;
  const hasComplaintWarning = complaintRate > 0.1;

  return (
    <div className="eo-tab-content">
      <div className="eo-tab-header">
        <div>
          <h3 className="eo-section-title">Deliverability & Domain Health</h3>
          <p className="eo-section-desc">
            Monitor real-time sender reputation, bounce rates, spam complaints, and automatic
            suppression halts across outreach campaigns.
          </p>
        </div>
        <div className="eo-filter-bar">
          <label className="eo-filter-label">Reporting Window:</label>
          <select
            className="eo-select eo-select-sm"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={7}>Last 7 Days</option>
            <option value={30}>Last 30 Days</option>
            <option value={90}>Last 90 Days</option>
          </select>
          <button className="eo-btn eo-btn-sm eo-btn-secondary" onClick={loadStats} title="Refresh">
            <i className="fas fa-sync-alt"></i>
          </button>
        </div>
      </div>

      {error && (
        <div className="eo-alert eo-alert-error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
          <button className="eo-alert-close" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}

      {loading ? (
        <div className="eo-loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Calculating deliverability metrics...</span>
        </div>
      ) : !stats ? (
        <div className="eo-empty-state">
          <p>No statistics available.</p>
        </div>
      ) : (
        <>
          {/* Top KPI Cards */}
          <div className="eo-stats-grid">
            <div className="eo-stat-card">
              <span className="eo-stat-label">Total Outbound Sent</span>
              <h2 className="eo-stat-value">{stats.sent || 0}</h2>
              <span className="eo-stat-sub">Across sequences & outreach</span>
            </div>

            <div className="eo-stat-card">
              <span className="eo-stat-label">Reply Rate</span>
              <h2 className="eo-stat-value eo-text-success">{replyRate}%</h2>
              <span className="eo-stat-sub">{stats.replies || 0} incoming replies</span>
            </div>

            <div className={`eo-stat-card ${hasBounceWarning ? 'eo-card-border-danger' : ''}`}>
              <div className="eo-card-metric-header">
                <span className="eo-stat-label">Bounce Rate</span>
                {hasBounceWarning && <span className="eo-badge-warn-sm">High</span>}
              </div>
              <h2 className={`eo-stat-value ${hasBounceWarning ? 'eo-text-danger' : ''}`}>
                {bounceRate}%
              </h2>
              <span className="eo-stat-sub">
                {stats.events?.bounce || 0} hard bounces (benchmark &lt; 2%)
              </span>
            </div>

            <div className={`eo-stat-card ${hasComplaintWarning ? 'eo-card-border-danger' : ''}`}>
              <div className="eo-card-metric-header">
                <span className="eo-stat-label">Spam Complaint Rate</span>
                {hasComplaintWarning && <span className="eo-badge-warn-sm">Action Needed</span>}
              </div>
              <h2 className={`eo-stat-value ${hasComplaintWarning ? 'eo-text-danger' : ''}`}>
                {complaintRate}%
              </h2>
              <span className="eo-stat-sub">
                {stats.events?.complaint || 0} complaints (benchmark &lt; 0.1%)
              </span>
            </div>

            <div className="eo-stat-card">
              <span className="eo-stat-label">Unsubscribe Rate</span>
              <h2 className="eo-stat-value">{unsubscribeRate}%</h2>
              <span className="eo-stat-sub">{stats.events?.unsubscribe || 0} opt-outs</span>
            </div>

            <div className="eo-stat-card">
              <span className="eo-stat-label">Active Suppressions</span>
              <h2 className="eo-stat-value eo-text-primary">{stats.activeSuppressions || 0}</h2>
              <span className="eo-stat-sub">Blocked in ledger (Deny-First)</span>
            </div>
          </div>

          {/* Secondary Details: Stop reasons and negative events */}
          <div className="eo-deliverability-details-grid">
            <div className="eo-card eo-breakdown-card">
              <h4 className="eo-card-title">Sequence Automation Stops</h4>
              <p className="eo-card-desc">
                Reason why active sequence enrollments were stopped automatically:
              </p>
              <div className="eo-breakdown-list">
                {Object.keys(stats.stoppedEnrollments || {}).length === 0 ? (
                  <p className="eo-empty-inline">No stopped enrollments in this period.</p>
                ) : (
                  Object.entries(stats.stoppedEnrollments).map(([reason, count]) => (
                    <div key={reason} className="eo-breakdown-row">
                      <span className="eo-breakdown-reason">
                        <i
                          className={
                            reason === 'reply'
                              ? 'fas fa-reply'
                              : reason === 'meeting_booked'
                                ? 'fas fa-calendar-check'
                                : reason === 'form_submitted'
                                  ? 'fas fa-file-alt'
                                  : 'fas fa-ban'
                          }
                        ></i>
                        {reason.replace(/_/g, ' ')}
                      </span>
                      <strong className="eo-breakdown-count">{count}</strong>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="eo-card eo-negative-events-card">
              <h4 className="eo-card-title">Recent Negative Signals</h4>
              <p className="eo-card-desc">
                Recipients suppressed automatically upon receiving bounce, complaint, or opt-out
                signals:
              </p>
              <div className="eo-events-list">
                {!stats.recentNegativeEvents || stats.recentNegativeEvents.length === 0 ? (
                  <p className="eo-empty-inline">
                    No hard bounces or complaints recorded recently.
                  </p>
                ) : (
                  stats.recentNegativeEvents.map((evt, idx) => (
                    <div key={idx} className="eo-negative-event-item">
                      <span className={`eo-event-badge ${evt.event_type}`}>{evt.event_type}</span>
                      <span className="eo-event-recipient">{evt.recipient_email}</span>
                      <span className="eo-event-date">
                        {new Date(evt.occurred_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DeliverabilityTab;
